import axios from 'axios';
import { apiClient } from '@/services/api';

export interface RemoteAvailability {
  includedInPlan: boolean;
  companyAssignmentPending: boolean;
  configured: boolean;
  reason: string;
  userEnabled: boolean;
}

export interface RemoteCompany {
  companyId: string;
  company: string;
  clientId: string;
}

export interface RemoteCompanyCredentials {
  organizationId: string;
  proxyCompanyId?: string;
  clientId: string;
  clientSecret: string;
}

export interface RemoteAdminContext {
  companies: RemoteCompany[];
  users: RemoteMember[];
  assignedProxyCompanyId: string | null;
}

export interface RemotePlanEligibility {
  planId: number | null;
  planName: string | null;
  enabled: boolean;
  allowed: boolean;
  reason: string;
  message: string;
}

export interface RemoteMember {
  userId: string;
  name: string;
  email: string;
  nationalId: string | null;
  remoteEnabled: boolean;
}

export type RemotePersonType = 'NATURAL' | 'JURIDICA';

export interface RemoteCertificate {
  certificateCode: string;
  personType: RemotePersonType;
  holderName: string;
  documentNumber: string | null;
  position: string | null;
  organization: string | null;
  ruc: string | null;
  issuer: string;
  notBefore: string;
  notAfter: string;
}

export interface RemoteCertificateList {
  authorizationRequired: boolean;
  certificates: RemoteCertificate[];
}

export interface RemoteAuthorizationStatus {
  requestId: string;
  status: 'PENDING' | 'PROCESSING' | 'AUTHORIZED' | 'FAILED' | 'EXPIRED';
  message: string | null;
}

export const REMOTE_RETURN_KEY = '360.remote-signing.return';
export const REMOTE_RETURN_PATH = '/remote-signing/return';

export interface RemoteOperation {
  id: string;
  status: string;
  expiresAt: string;
  message: string | null;
  authorizationUrl: string | null;
  documentNames: string[];
}

export const remoteDate = (value: string | null) => value ? new Intl.DateTimeFormat('es-PE', {
  dateStyle: 'medium', timeStyle: 'short',
}).format(new Date(value)) : '—';

/** Solo rutas internas de 360: evita redirecciones abiertas tras volver de Camercloud. */
const internalPath = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') && !value.includes('\\');

export function pendingRemoteReturn(): { requestId: string; userId: string; returnTo?: string; kind?: 'sign' } | null {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(REMOTE_RETURN_KEY) || 'null');
    if (typeof value !== 'object' || value === null || !('requestId' in value) || !('userId' in value)) return null;
    if (typeof value.requestId !== 'string' || typeof value.userId !== 'string') return null;
    const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuid.test(value.requestId) || !uuid.test(value.userId)) return null;
    return { requestId: value.requestId, userId: value.userId,
      ...('returnTo' in value && internalPath(value.returnTo) ? { returnTo: value.returnTo } : {}),
      ...('kind' in value && value.kind === 'sign' ? { kind: 'sign' as const } : {}),
    };
  } catch { return null; }
}

export function afterLoginPath(userId?: string): string {
  return userId && pendingRemoteReturn()?.userId === userId ? REMOTE_RETURN_PATH : '/dashboard';
}

export function remoteError(error: unknown): string {
  if (axios.isAxiosError<{ message?: string }>(error) && error.response?.data?.message) return error.response.data.message;
  return error instanceof Error ? error.message : 'No se pudo completar la solicitud. Inténtalo nuevamente.';
}

export const remoteSigningApi = {
  availability: async () => (await apiClient.get<{ remoteSigning?: RemoteAvailability }>('/users/me')).data.remoteSigning,
  adminContext: async (organizationId: string) =>
    (await apiClient.get<RemoteAdminContext>('/remote-signing/admin/context', { params: { organizationId } })).data,
  eligibility: async (organizationId: string) =>
    (await apiClient.get<RemotePlanEligibility>('/remote-signing/admin/eligibility', { params: { organizationId } })).data,
  configureCompany: async (payload: RemoteCompanyCredentials) =>
    (await apiClient.post<RemoteCompany>('/remote-signing/admin/companies', payload)).data,
  enableMember: async (organizationId: string, userId: string, proxyCompanyId: string) =>
    (await apiClient.post<RemoteMember>('/remote-signing/admin/members', { organizationId, userId, proxyCompanyId })).data,
  disableMember: async (organizationId: string, userId: string) =>
    (await apiClient.delete<RemoteMember>(`/remote-signing/admin/members/${userId}`, { params: { organizationId } })).data,
  certificates: async () => (await apiClient.get<RemoteCertificateList>('/remote-signing/certificates')).data,
  /** Abre Camercloud para autorizar la consulta de certificados y vuelve a `returnTo`. */
  authorize: async (userId: string, returnTo: string) => {
    const { data } = await apiClient.post<{ requestId: string; authorizationUrl: string; expiresAt: string }>(
      '/remote-signing/certificates/authorize');
    sessionStorage.setItem(REMOTE_RETURN_KEY, JSON.stringify({ requestId: data.requestId, userId, returnTo }));
    window.location.assign(data.authorizationUrl);
  },
  status: async (id: string) =>
    (await apiClient.get<RemoteAuthorizationStatus>(`/remote-signing/authorizations/${id}`)).data,
  startSigning: async (requestId: string, userId: string, certificateCode: string, signatureIds: string[]) => {
    sessionStorage.setItem(REMOTE_RETURN_KEY, JSON.stringify({ requestId, userId, kind: 'sign' }));
    const { data } = await apiClient.post<RemoteOperation>('/remote-signing/operations', { requestId, certificateCode, signatureIds });
    window.location.assign(data.authorizationUrl || REMOTE_RETURN_PATH);
  },
  operation: async (id: string) => (await apiClient.get<RemoteOperation>(`/remote-signing/operations/${id}`)).data,
  completeSigning: async (id: string) => (await apiClient.post<RemoteOperation>(`/remote-signing/operations/${id}/complete`)).data,
  cancelSigning: async (id: string) => (await apiClient.post<RemoteOperation>(`/remote-signing/operations/${id}/cancel`)).data,
};
